import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, LinearProgress, Theme, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Add, PlaylistAddCheck, Refresh } from '@material-ui/icons';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { AlertCard } from '../../../../common/cards/alert-card/AlertCard';
import {
  CREATE_ACTIVITY_GET_PROGRAMS_V2,
  CREATE_ACTIVITY_GET_REPORT_TYPES_V2,
  GET_ACTIVITY_MANAGEMENT_ACTIVITY_TAGS,
} from '../activity-form-v2/activity-form-model-v2.gql';
import {
  CreateActivityGetProgramsV2,
  CreateActivityGetProgramsV2Variables,
} from '../activity-form-v2/__generated__/CreateActivityGetProgramsV2';
import { CreateActivityGetReportTypesV2 } from '../activity-form-v2/__generated__/CreateActivityGetReportTypesV2';
import { GetActivityManagementActivityTags } from '../activity-form-v2/__generated__/GetActivityManagementActivityTags';
import { useSaveActivityFetch } from '../activity-form-v2/activity-form-actions-v2';
import { useSaveRosterTemplate } from '../../rosters/roster-templates-screen/roster-template-actions';
import { useAddBulkOneOffSessions } from '../../rosters/common/add-one-off-session-api';
import { ImportActivityRow, ImportLookups, RowErrors } from './activity-import-types';
import { createEmptyRow, CSV_FORMAT_ERROR, parseActivityCsv } from './activity-import-parser';
import { getRowOneOffCount, getRowSessionCount, normaliseKey, validateRow } from './activity-import-validation';
import { ONE_OFF_FIELDS, oneOffKey, sessionKey, SESSION_FIELDS } from './activity-import-columns';
import { buildActivityPayload } from './activity-import-payload';
import { useAddressGeocoder } from './useAddressGeocoder';
import { UploadActivitiesStep } from './steps/UploadActivitiesStep';
import { ActivitiesGrid } from './components/ActivitiesGrid';
import { AddressCellDialog, LocationSelection } from './components/AddressCellDialog';
import { ActivityDrawer } from './components/ActivityDrawer';
import { StatusIcon } from './components/status-icon';
import { BUTTON_ICON_STYLE, getPrimaryButtonStyle, getToolbarButtonStyle } from './import-button-styles';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: '12px 16px 16px',
    '& > * + *': {
      marginTop: 12,
    },
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    '& > * + *': {
      marginLeft: 10,
    },
  },
  title: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#333333',
    marginRight: 'auto',
  },
  button: getToolbarButtonStyle(theme),
  primaryButton: getPrimaryButtonStyle(theme),
  summary: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    '& > *': {
      marginRight: 16,
    },
    fontSize: '13px',
    color: theme.color.grey.neutral300,
    '& b': {
      color: theme.color.grey.neutral500,
      fontWeight: 600,
    },
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    '& > * + *': {
      marginLeft: 6,
    },
  },
  toggle: {
    marginLeft: 'auto',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    '& input': {
      accentColor: theme.color.secondary.main900,
      margin: '0 8px 0 0',
    },
  },
  workspace: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    '& > * + *': {
      marginLeft: 12,
    },
  },
  footnote: {
    fontSize: '12px',
    color: theme.color.grey.neutral200,
  },
}));

const toLookup = (items: { id: string; name: string }[]) =>
  new Map(items.map((item) => [normaliseKey(item.name), item.id] as [string, string]));

const getErrorMessage = (response: any, fallback: string) => response?.data?.message || fallback;

const removeSlot = (
  values: Record<string, string>,
  n: number,
  count: number,
  suffixes: string[],
  key: (m: number, suffix: string) => string
) => {
  const next = { ...values };
  for (let m = n; m <= count; m += 1) {
    suffixes.forEach((suffix) => {
      const value = next[key(m + 1, suffix)];
      if (value) next[key(m, suffix)] = value;
      else delete next[key(m, suffix)];
    });
  }
  return next;
};

export function ImportingActivitiesScreen() {
  const classes = useStyles();
  const { setSnackbar } = useSnackbar();
  const [step, setStep] = useState<'upload' | 'grid'>('upload');
  const [rows, setRows] = useState<ImportActivityRow[]>([]);
  const [ignoredHeaders, setIgnoredHeaders] = useState<string[]>([]);
  const [parseError, setParseError] = useState<string>();
  const [addressRowId, setAddressRowId] = useState<string>();
  const [isCreating, setIsCreating] = useState(false);
  const [showOnlyIssues, setShowOnlyIssues] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<string>();
  const geocodingRef = useRef(false);

  const geocode = useAddressGeocoder();
  const saveActivity = useSaveActivityFetch();
  const saveRosterTemplate = useSaveRosterTemplate();
  const { addBulkOneOffSessions } = useAddBulkOneOffSessions();

  const { data: programsData, loading: programsLoading } = useCampfireQuery<
    CreateActivityGetProgramsV2,
    CreateActivityGetProgramsV2Variables
  >(CREATE_ACTIVITY_GET_PROGRAMS_V2, { options: { variables: { activityId: '' } } });
  const { data: reportTypesData, loading: reportTypesLoading } = useCampfireQuery<CreateActivityGetReportTypesV2, {}>(
    CREATE_ACTIVITY_GET_REPORT_TYPES_V2
  );
  const { data: tagsData, loading: tagsLoading } = useCampfireQuery<GetActivityManagementActivityTags, {}>(
    GET_ACTIVITY_MANAGEMENT_ACTIVITY_TAGS
  );

  const lookups: ImportLookups = useMemo(() => {
    const programs = (programsData?.vm.programs ?? []).filter((program) => !program.dateSuspended);
    const reportTypes = reportTypesData?.vm.reportTypes ?? [];
    return {
      programs: toLookup(programs.map((program) => ({ id: program.programId, name: program.name }))),
      tags: toLookup((tagsData?.vm.activityTags ?? []).map((tag) => ({ id: tag.activityTagId, name: tag.name }))),
      roles: toLookup(
        (reportTypesData?.vm.activityRoles ?? []).map((role) => ({ id: role.activityRoleId, name: role.name }))
      ),
      reportTypes: toLookup(reportTypes.map((reportType) => ({ id: reportType.reportTypeId, name: reportType.name }))),
      programNames: programs.map((program) => program.name).sort((a, b) => a.localeCompare(b)),
      reportTypeNames: reportTypes.map((reportType) => reportType.name).sort((a, b) => a.localeCompare(b)),
    };
  }, [programsData, reportTypesData, tagsData]);

  const lookupsLoading = programsLoading || reportTypesLoading || tagsLoading;

  const errors = useMemo(
    () =>
      rows.reduce((acc, row) => {
        acc[row.id] = row.createStatus === 'created' ? {} : validateRow(row, lookups);
        return acc;
      }, {} as Record<string, RowErrors>),
    [rows, lookups]
  );

  const updateRow = useCallback((rowId: string, update: (row: ImportActivityRow) => ImportActivityRow) => {
    setRows((prev) => prev.map((row) => (row.id === rowId ? update(row) : row)));
  }, []);

  useEffect(() => {
    if (geocodingRef.current) return;
    const next = rows.find((row) => row.geocodeStatus === 'pending');
    if (!next) return;

    geocodingRef.current = true;
    const query = next.values.address;
    geocode(query).then((address) => {
      geocodingRef.current = false;
      updateRow(next.id, (row) =>
        row.values.address !== query
          ? row
          : { ...row, resolvedAddress: address, geocodeStatus: address ? 'resolved' : 'failed' }
      );
    });
  }, [rows, geocode, updateRow]);

  const handleUpload = (file: File) => {
    setParseError(undefined);
    parseActivityCsv(file)
      .then((result) => {
        if (result.rows.length === 0) {
          setParseError('No activities found in this file');
          return;
        }
        setRows(result.rows);
        setIgnoredHeaders(result.ignoredHeaders);
        setStep('grid');
      })
      .catch((error) =>
        setParseError(
          error?.name === CSV_FORMAT_ERROR ? error.message : 'Unable to read this file. Check it is a valid CSV'
        )
      );
  };

  const handleStartBlank = () => {
    setRows([createEmptyRow()]);
    setIgnoredHeaders([]);
    setStep('grid');
  };

  const handleCellChange = useCallback(
    (rowId: string, key: string, value: string) => {
      updateRow(rowId, (row) => {
        const values = { ...row.values };
        if (value) values[key] = value;
        else delete values[key];
        return {
          ...row,
          values,
          createStatus: row.createStatus === 'failed' ? 'idle' : row.createStatus,
          createError: undefined,
        };
      });
    },
    [updateRow]
  );

  const handleLocationSave = ({ address, remoteDetails }: LocationSelection) => {
    if (!addressRowId) return;
    updateRow(addressRowId, (row) => {
      const values = { ...row.values };
      if (address) values.address = address.formatted;
      else delete values.address;
      if (remoteDetails) values.remoteDetails = remoteDetails;
      else delete values.remoteDetails;
      return {
        ...row,
        values,
        resolvedAddress: address,
        geocodeStatus: address ? 'resolved' : 'idle',
      };
    });
    setAddressRowId(undefined);
  };

  const handleDelete = useCallback((rowId: string) => {
    setRows((prev) => prev.filter((row) => row.id !== rowId));
    setSelectedRowId(undefined);
  }, []);

  const handleAddSession = useCallback(
    (rowId: string) => updateRow(rowId, (row) => ({ ...row, sessionSlots: getRowSessionCount(row) + 1 })),
    [updateRow]
  );

  const handleRemoveSession = useCallback(
    (rowId: string, n: number) =>
      updateRow(rowId, (row) => {
        const count = getRowSessionCount(row);
        const suffixes = SESSION_FIELDS.map((field) => field.suffix);
        const values = removeSlot(row.values, n, count, suffixes, sessionKey);
        return { ...row, values, sessionSlots: Math.max(1, count - 1) };
      }),
    [updateRow]
  );

  const handleAddOneOff = useCallback(
    (rowId: string) => updateRow(rowId, (row) => ({ ...row, oneOffSlots: getRowOneOffCount(row) + 1 })),
    [updateRow]
  );

  const handleRemoveOneOff = useCallback(
    (rowId: string, n: number) =>
      updateRow(rowId, (row) => {
        const count = getRowOneOffCount(row);
        const suffixes = ONE_OFF_FIELDS.map((field) => field.suffix);
        const values = removeSlot(row.values, n, count, suffixes, oneOffKey);
        return { ...row, values, oneOffSlots: Math.max(0, count - 1) };
      }),
    [updateRow]
  );

  const sessionColumnCount = rows.reduce((max, row) => Math.max(max, getRowSessionCount(row) + 1), 2);
  const oneOffColumnCount = rows.reduce((max, row) => Math.max(max, getRowOneOffCount(row) + 1), 1);

  const handleAddActivity = () => {
    const row = createEmptyRow();
    setRows((prev) => [...prev, row]);
    setSelectedRowId(row.id);
  };

  const readyRows = rows.filter(
    (row) =>
      row.createStatus !== 'created' &&
      Object.keys(errors[row.id] ?? {}).length === 0 &&
      row.geocodeStatus !== 'pending'
  );
  const issueCount = rows.filter((row) => Object.keys(errors[row.id] ?? {}).length > 0).length;
  const createdCount = rows.filter((row) => row.createStatus === 'created').length;
  const failedCount = rows.filter((row) => row.createStatus === 'failed').length;
  const pendingGeocodes = rows.filter((row) => row.geocodeStatus === 'pending').length;

  const createRow = async (row: ImportActivityRow) => {
    const { activity, rosterDates, oneOffSessions } = buildActivityPayload(row, lookups);
    let { activityId } = row;

    if (!activityId) {
      const response: any = await saveActivity.run(activity);
      if (!response?.ok) {
        updateRow(row.id, (r) => ({
          ...r,
          createStatus: 'failed',
          createError: getErrorMessage(response, 'Unable to create activity'),
        }));
        return;
      }
      activityId = response.data.data.activityId as string;
      updateRow(row.id, (r) => ({ ...r, activityId }));
    }

    if (rosterDates.length > 0) {
      const response: any = await saveRosterTemplate.run({
        activityId,
        rosterDates,
        location: null,
        remoteLocation: null,
      });
      if (!response?.ok) {
        updateRow(row.id, (r) => ({
          ...r,
          createStatus: 'failed',
          createError: `Activity created, but rosters could not be added: ${getErrorMessage(
            response,
            'unknown error'
          )}. Retry to add them.`,
        }));
        return;
      }
    }

    if (oneOffSessions.length > 0) {
      const response: any = await addBulkOneOffSessions(activityId, oneOffSessions);
      if (!response?.ok) {
        updateRow(row.id, (r) => ({
          ...r,
          createStatus: 'failed',
          createError: `Activity created, but one-off sessions could not be added: ${getErrorMessage(
            response,
            'unknown error'
          )}. Retry to add them.`,
        }));
        return;
      }
    }

    updateRow(row.id, (r) => ({ ...r, createStatus: 'created', createError: undefined }));
  };

  const handleCreate = async () => {
    const targets = readyRows;
    setIsCreating(true);
    // eslint-disable-next-line no-restricted-syntax
    for (const row of targets) {
      updateRow(row.id, (r) => ({ ...r, createStatus: 'creating' }));
      try {
        // eslint-disable-next-line no-await-in-loop
        await createRow(row);
      } catch {
        updateRow(row.id, (r) => ({ ...r, createStatus: 'failed', createError: 'Unable to create activity' }));
      }
    }
    setIsCreating(false);
    setSnackbar({ open: true, message: 'Import finished', variant: 'success' });
  };

  if (step === 'upload') {
    return <UploadActivitiesStep onUpload={handleUpload} onStartBlank={handleStartBlank} parseError={parseError} />;
  }

  const visibleRows = showOnlyIssues
    ? rows.filter((row) => Object.keys(errors[row.id] ?? {}).length > 0 || row.createStatus === 'failed')
    : rows;

  const selectedRow = rows.find((row) => row.id === selectedRowId);
  const addressRow = rows.find((row) => row.id === addressRowId);

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography className={classes.title}>Import Activities</Typography>
        <Button
          className={classes.button}
          startIcon={<Refresh style={BUTTON_ICON_STYLE} />}
          disabled={isCreating}
          onClick={() => {
            setRows([]);
            setSelectedRowId(undefined);
            setStep('upload');
          }}
        >
          Start over
        </Button>
        <Button
          className={classes.button}
          startIcon={<Add style={BUTTON_ICON_STYLE} />}
          disabled={isCreating}
          onClick={handleAddActivity}
        >
          Add activity
        </Button>
        <Button
          className={classes.primaryButton}
          startIcon={<PlaylistAddCheck style={BUTTON_ICON_STYLE} />}
          disabled={isCreating || lookupsLoading || readyRows.length === 0}
          onClick={handleCreate}
        >
          {isCreating
            ? 'Creating...'
            : `Create ${readyRows.length} ${readyRows.length === 1 ? 'activity' : 'activities'}`}
        </Button>
      </Box>

      {(lookupsLoading || isCreating) && <LinearProgress />}

      {ignoredHeaders.length > 0 && (
        <AlertCard variant='warning' title='Some columns were not recognised and have been ignored'>
          <Typography variant='body2'>{ignoredHeaders.join(', ')}</Typography>
        </AlertCard>
      )}

      <Box className={classes.summary}>
        <span>
          <b>{rows.length}</b> {rows.length === 1 ? 'activity' : 'activities'}
        </span>
        <span className={classes.pill}>
          <StatusIcon kind='ready' size={14} />
          <span>
            <b>{readyRows.length}</b> ready
          </span>
        </span>
        <span className={classes.pill}>
          <StatusIcon kind='issues' size={14} />
          <span>
            <b>{issueCount}</b> with issues
          </span>
        </span>
        <span className={classes.pill}>
          <StatusIcon kind='created' size={14} />
          <span>
            <b>{createdCount}</b> created
          </span>
        </span>
        {failedCount > 0 && (
          <span className={classes.pill}>
            <StatusIcon kind='failed' size={14} />
            <span>
              <b>{failedCount}</b> failed
            </span>
          </span>
        )}
        {pendingGeocodes > 0 && <span>Looking up {pendingGeocodes} addresses...</span>}
        <label className={classes.toggle} htmlFor='activity-import-only-issues'>
          <input
            id='activity-import-only-issues'
            type='checkbox'
            checked={showOnlyIssues}
            onChange={(e) => setShowOnlyIssues(e.target.checked)}
          />
          Only rows with issues
        </label>
      </Box>

      <Box className={classes.workspace}>
        <ActivitiesGrid
          rows={visibleRows}
          errors={errors}
          sessionCount={sessionColumnCount}
          oneOffCount={oneOffColumnCount}
          programNames={lookups.programNames}
          reportTypeNames={lookups.reportTypeNames}
          selectedRowId={selectedRowId}
          locked={isCreating}
          onChange={handleCellChange}
          onSelectRow={setSelectedRowId}
          onAddressClick={setAddressRowId}
        />
        {selectedRow && (
          <ActivityDrawer
            row={selectedRow}
            errors={errors[selectedRow.id] ?? {}}
            programNames={lookups.programNames}
            reportTypeNames={lookups.reportTypeNames}
            locked={isCreating}
            onChange={handleCellChange}
            onAddressClick={setAddressRowId}
            onAddSession={handleAddSession}
            onRemoveSession={handleRemoveSession}
            onAddOneOff={handleAddOneOff}
            onRemoveOneOff={handleRemoveOneOff}
            onDelete={handleDelete}
            onClose={() => setSelectedRowId(undefined)}
          />
        )}
      </Box>

      <Typography className={classes.footnote}>
        Click a cell to edit it, or click the status icon to open the full details. Hover an underlined cell to see what
        needs fixing.
      </Typography>

      {addressRowId && !isCreating && (
        <AddressCellDialog
          open
          activityName={addressRow?.values.activityName ?? ''}
          importedAddress={addressRow?.values.address ?? ''}
          resolvedAddress={addressRow?.resolvedAddress ?? null}
          geocodeStatus={addressRow?.geocodeStatus ?? 'idle'}
          remoteDetails={addressRow?.values.remoteDetails ?? ''}
          onClose={() => setAddressRowId(undefined)}
          onSave={handleLocationSave}
        />
      )}
    </Box>
  );
}
